import { useState, useEffect } from "react"
import CustomizeModal from "./CustomizeModal.client"
import { CustomerAccount, CustomerMetafields, ImageItem, ProductFilterObject, ProductMetafields, ShopMetafields, ShopSettings, VariantEdgeMetafields } from "~/types"
import { CartDrawer } from "../../global/CartDrawer.client"
import { useDrawer } from "../../global/Drawer.client"
import { useCart } from "@shopify/hydrogen"
import { CartLineInput } from "@shopify/hydrogen/storefront-api-types"
import ProductsBreadcrumb from "../ProductsBreadcrumb.client"
import ZoomImageModal from "./ZoomImageModal.client"
import ProductImage from "./ProductImage.client"
import ProductInfo from "./ProductInfo.client"
import RecommendedProducts from "./RecommendedProducts.client"
import NoImage from '~/assets/no_image_big.jpg';
import { buttonBackToTop, comments, customBannerPDP, multiStore, quantityCustom, sectionRecommendedProducts, setupSectionBelow, stockRequestInfo } from "../../../../setup/settings/__product"
import ProductDescription from "../description/ProductDescription.client"
import NoImageMultiStore from '~/assets/missingImage.jpg';
import BackToTopButton from "~/rs-components/elements/BackToTop.client"
import SetupCustomSectionBelow from "../../../../setup/components/setup/productDetail/SetupCustomSectionBelow.client"
import Comments from "../comments/Comments.client"
import SetupCustomBanner from "../../../../setup/components/setup/productDetail/SetupCustomBanner.client"

type ProductDetailsProps =
    {
        product: ProductMetafields,
        featuredProducts: ProductFilterObject[],
        shop: ShopMetafields,
        customer: CustomerMetafields,
        customerAccounts: CustomerAccount[],
    }

export default function ProductDetails({ product, featuredProducts, customer, customerAccounts, shop }: ProductDetailsProps) {
    const settings = shop.settings?.value ? shop.settings?.value : "[]";
    const currency = shop.moneyFormat;
    const variantStock = product.variants.edges.filter(variant => variant.node?.title != 'DefaultVariant' && variant.node?.showInProductPage != null && variant.node?.showInProductPage?.value != 'false') as VariantEdgeMetafields[];
    const variantDefault = product.variants.edges.filter(variant => variant.node?.title == 'DefaultVariant') as VariantEdgeMetafields[];
    const minimumQuantity: number = quantityCustom.visible && product.minimumQuantity?.value != null ? parseInt(product.minimumQuantity?.value) : 1;
    const multiplierQuantity: number = quantityCustom.visible && product.multiplierQuantity?.value != null ? parseInt(product.multiplierQuantity?.value) : 1;
    const images: ImageItem[] = product.images?.value != null ? JSON.parse(product.images?.value) : [];
    const defaultImage = images != null && images.length > 0 ? images[0].Src : (multiStore ? NoImageMultiStore : NoImage);
    const [activeImage, setActiveImage] = useState<string>(defaultImage);
    const [selectedVariant, setSelectedVariant] = useState(variantStock[0]?.node.id);
    const [showCustomize, setShowCustomize] = useState(false);
    const [variant, setVariant] = useState<VariantEdgeMetafields | undefined>(undefined);
    const [modalType, setModalType] = useState<number>(0);
    const isStock: boolean = variantStock.length > 0;
    const shopSettings: ShopSettings = settings != null && settings != "" ? JSON.parse(settings) : JSON.parse("{}");

    const [stockError, setStockError] = useState<string | null>(null);

    useEffect(() => {
        if (showCustomize) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'auto';
    }, [showCustomize])

    useEffect(() => {
        setActiveImage(defaultImage)
    }, [defaultImage])

    const hideModal = () => {
        // document.body.style.overflow = 'unset';
        setShowCustomize(false);
    }

    const {
        isOpen: isCartOpen,
        openDrawer: openCart,
        closeDrawer: closeCart,
    } = useDrawer();

    const cart = useCart()

    const addVariantBasket = async (variant: VariantEdgeMetafields) => {
        const stock = variant?.node.quantityAvailable
            ? Number(variant.node.quantityAvailable)
            : 0;
        if (stockRequestInfo.enabled) {
            if (stock === 0) {
                setStockError("Sorry, there are no items available.");
                return;
            }

            const stockCheckResult = await checkStockAvailability(`${variant.node.id}`, stock);
            if (!stockCheckResult) return;
        }
        setSelectedVariant(variant.node.id);
        onAddToBasket(1);
        hideModal();
    }

    const checkStockAvailability = async (merchandiseId: string, stockShopify: number): Promise<boolean> => {
        try {
            setStockError(null);
            const variantId = merchandiseId.split('/').pop();
            const response = await fetch(`/externalapi/stock?variantId=${variantId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const stockAvailable = await response.json();

            if (response.status !== 200 || stockAvailable === null) {
                return true;
            }

            if (stockAvailable === 0) {
                return handleStockError(variantId, `Sorry, there are no items available.`);
            }

            if (stockAvailable !== stockShopify) {
                const update = await fetch(`/externalapi/stock`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ variantId: variantId }),
                });

                if (!update.ok) {
                    const updateErrorText = await update.text();
                    console.error(`Error updating stock: ${update.status} - ${updateErrorText}`);
                    setStockError('Error updating stock availability.');
                    return false;
                }
            }

            return true;

        } catch (error) {
            console.error('Error checking stock availability:', error);
            setStockError('An unexpected error occurred. Please try again later.');
            return false;
        }
    };

    const handleStockError = async (variantId: any, errorMessage: string): Promise<boolean> => {
        setStockError(errorMessage);
        const update = await fetch(`/externalapi/stock`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ variantId: variantId }),
        });

        if (!update.ok) {
            const updateErrorText = await update.text();
            console.error(`Error updating stock: ${update.status} - ${updateErrorText}`);
            setStockError('Error updating stock availability.');
            return false;
        }

        return false;
    };

    const onAddToBasket = async (quantity: number) => {
        const merchandiseId = selectedVariant ? `${selectedVariant}` : variantDefault[0]?.node.id;

        if (!quantityCustom.visible) {
            const line: CartLineInput = {
                merchandiseId,
                quantity: 1
            }
            cart.linesAdd([line])
            openCart();
        }
        if (quantityCustom.visible && quantity >= minimumQuantity && quantity % multiplierQuantity == 0) {
            const line: CartLineInput = {
                merchandiseId,
                quantity: quantity,
                attributes: [
                    {
                        key: "multiplierQuantity",
                        value: `${multiplierQuantity}`
                    },
                    {
                        key: "minimumQuantity",
                        value: `${minimumQuantity}`
                    }
                ]
            }
            cart.linesAdd([line])
            openCart();
        }
    }

    const [showImage, setShowImage] = useState(false);
    return (
        <>
            <ZoomImageModal showImage={showImage} setShowImage={setShowImage} activeImage={activeImage} />
            <CartDrawer isOpen={isCartOpen} onClose={closeCart} currency={currency} customerAccounts={customerAccounts} />
            <CustomizeModal
                currency={currency}
                addVariantBasket={addVariantBasket}
                variant={variant}
                product={product}
                show={showCustomize}
                close={hideModal}
                openCart={openCart}
                modalType={modalType}
                shopSettings={shopSettings}
                stockErrorMessage={stockError}
            />
            <ProductsBreadcrumb className="breadcrumb px-5 marginProduct" />
            <div className="product-container container px-4">
                {
                    customBannerPDP.enabled && <SetupCustomBanner product={product} shop={shop} />
                }
                <div className="grid grid-cols-1 sm:grid-cols-2">
                    <ProductImage
                        setShowImage={setShowImage}
                        setActiveImage={setActiveImage}
                        images={images}
                        activeImage={activeImage}
                        product={product}
                    />
                    <ProductInfo
                        product={product}
                        shopSettings={shopSettings}
                        currency={currency}
                        setVariant={setVariant}
                        setShowCustomize={setShowCustomize}
                        setSelectedVariant={setSelectedVariant}
                        selectedVariant={selectedVariant}
                        onAddToBasket={onAddToBasket}
                        featuredProducts={featuredProducts}
                        show={showCustomize}
                        customer={customer}
                        setModalType={setModalType}
                        isStock={isStock}
                        variants={variantStock}
                        variantDefault={variantDefault}
                    />
                </div>
                <ProductDescription product={product} shopSettings={shopSettings}></ProductDescription>

                {buttonBackToTop.productDetailEnabled && <BackToTopButton />}

            </div>
            {sectionRecommendedProducts.enabled &&
                < div className="pre-footer">
                    {featuredProducts.length > 0 &&
                        <RecommendedProducts product={product} featuredProducts={featuredProducts} settings={settings} currency={currency} />
                    }
                </div >
            }
            {setupSectionBelow.enabled &&
                <SetupCustomSectionBelow shop={shop} brand={product.vendor} productRange={product.productType} />
            }
            {comments.enabled &&
                 <Comments product={product} customer={customer}/>
            }
        </>
    )
}